/**
 * Register MCP Server — multi-mode wizard (Phase 2.A.4).
 *
 * Three onboarding modes:
 *
 *   M-Endpoint  — server runs elsewhere; admin pastes the URL. We register
 *                 a row pointing at it. Admin confirms the server has
 *                 already been configured per docs/server-authoring/02
 *                 (P-A trust check against gateway JWKS).
 *
 *   M-Hosted    — gateway operator runs the server on the EC2 box. UI
 *                 collects what we need (slug, port, etc.) + flags to the
 *                 operator that the systemd unit needs provisioning via
 *                 `infra/deploy/04-add-mcp-server.sh` after the row lands.
 *
 *   REST wrapper — admin uploads a JSON spec describing N upstream APIs.
 *                  Backend auto-creates the server row + tool rows; the
 *                  rest-wrapper-service (Phase 2.A.4b) serves them at
 *                  /wrappers/<slug>/mcp.
 */
import { useEffect, useState, type JSX } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, Globe2, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useUrlEnum } from '@/hooks/useUrlState.js';

import {
  ApiError,
  useCreateServer,
  useTestConnection,
  useValidateServer,
  type TestConnectionResult,
  useWorkgroups,
  type CheckResult,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { HelpCallout } from '@/components/console/HelpCallout.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { Required, RequiredLegend } from '@/components/console/Required.js';
import { SectionCard } from '@/components/console/SectionCard.js';

type Mode = 'endpoint' | 'wrapper';
const MODES = ['endpoint', 'wrapper'] as const;

const RISK_TIERS = ['low', 'medium', 'high', 'critical'] as const;

export default function Register(): JSX.Element {
  const navigate = useNavigate();
  const wgs = useWorkgroups({ limit: 500 });
  const createServer = useCreateServer();

  // Mode lives in the URL so back/forward, the left-nav "Register Server"
  // link, and bookmarks all behave correctly. `useUrlEnum` returns the
  // sentinel default when the param is absent or invalid.
  const [modeParam, setModeParam] = useUrlEnum('mode', [...MODES, 'pick'], 'pick');
  const mode: Mode | null = modeParam === 'pick' ? null : (modeParam as Mode);
  const setMode = (m: Mode | null): void => setModeParam(m ?? 'pick');

  if (mode === null) {
    return <ModePicker onPick={setMode} />;
  }

  return (
    <>
      <PageHeader
        eyebrow="Build"
        title="Register MCP Server"
        description={
          mode === 'endpoint'
            ? 'M-Endpoint — register a server you operate elsewhere.'
            : 'REST wrapper — auto-generate an MCP server from a JSON spec describing N upstream APIs.'
        }
      >
        <ActionButton icon={ArrowLeft} onClick={() => setMode(null)}>
          Change mode
        </ActionButton>
      </PageHeader>

      {mode === 'endpoint' && (
        <EndpointWizard
          wgs={wgs.data?.items ?? []}
          submit={async (input) => {
            const row = await createServer.mutateAsync(input);
            navigate(`/build/servers/${row.id}`);
          }}
          submitting={createServer.isPending}
        />
      )}

      {mode === 'wrapper' && <WrapperRedirect />}
    </>
  );
}

/* ─── Wrapper mode redirects to the structured editor ────────────── */
function WrapperRedirect(): JSX.Element {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/build/wrappers/new', { replace: true });
  }, [navigate]);
  return <p className="subtle">Redirecting to the wrapper editor…</p>;
}

/* ─── Mode picker ─────────────────────────────────────────────────── */

function ModePicker({ onPick }: { onPick: (m: Mode) => void }): JSX.Element {
  return (
    <>
      <PageHeader
        eyebrow="Build"
        title="Register MCP Server"
        description="Pick the onboarding mode for this server. Each gives a different deploy + ownership story — see docs/server-authoring/06-deployment-models.md."
      />

      <div style={{ marginBottom: 12 }}>
        <HelpCallout
          title="Which mode should I pick?"
          whatHappens={
            <>
              Both modes register an MCP server with the gateway and make its tools discoverable
              through the catalog. The difference is who runs the actual server and how your tools
              talk to upstream services.
            </>
          }
          youNeed={[
            <>
              <strong>M-Endpoint</strong> if your team already has (or will write) an MCP server
              that speaks JSON-RPC over Streamable HTTP. Best for native MCP servers, agents, or
              when you need custom logic between MCP and your backend.
            </>,
            <>
              <strong>REST wrapper</strong> if you have one or more existing REST APIs and want to
              expose them as MCP tools without writing or deploying any new code. The gateway hosts
              the wrapper for you.
            </>,
          ]}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 12,
        }}
      >
        <ModeCard
          icon={<Globe2 size={20} />}
          title="M-Endpoint"
          subtitle="My team runs the MCP server"
          when="Your server already speaks MCP and you can give us a URL. You handle the P-A trust check (docs/server-authoring/02) on your side."
          onClick={() => onPick('endpoint')}
        />
        <ModeCard
          icon={<Wand2 size={20} />}
          title="REST wrapper (auto-gen)"
          subtitle="I have N REST APIs, wrap them as MCP"
          when="Upload a JSON spec describing each tool's upstream URL + auth + schema. The gateway's wrapper service auto-serves them as MCP — no code to write or deploy."
          onClick={() => onPick('wrapper')}
        />
      </div>
    </>
  );
}

function ModeCard({
  icon,
  title,
  subtitle,
  when,
  onClick,
}: {
  icon: JSX.Element;
  title: string;
  subtitle: string;
  when: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: 18,
        border: '1px solid var(--line)',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.5)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {icon}
        <strong style={{ fontSize: 16 }}>{title}</strong>
      </div>
      <span className="subtle" style={{ fontSize: 13 }}>
        {subtitle}
      </span>
      <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>{when}</p>
      <span style={{ alignSelf: 'flex-end', fontSize: 13, color: 'var(--accent)' }}>
        Continue <ArrowRight size={14} aria-hidden style={{ verticalAlign: 'middle' }} />
      </span>
    </button>
  );
}

/* ─── M-Endpoint wizard ───────────────────────────────────────────── */

interface ServerInput {
  slug: string;
  name: string;
  description?: string;
  owning_wg_id: string;
  backend_type: 'native_mcp' | 'rest_wrapper';
  backend_url: string;
  risk_tier: 'low' | 'medium' | 'high' | 'critical';
}

function EndpointWizard({
  wgs,
  submit,
  submitting,
}: {
  wgs: { id: string; display_name: string }[];
  submit: (input: ServerInput) => Promise<void>;
  submitting: boolean;
}): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [url, setUrl] = useState('');
  const [wg, setWg] = useState('');
  const [risk, setRisk] = useState<(typeof RISK_TIERS)[number]>('low');
  const [trustConfirmed, setTrustConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const validate = useValidateServer();
  const checks = validate.data?.checks ?? null;
  const testConn = useTestConnection();
  const testResult = testConn.data ?? null;
  useEffect(() => {
    if (!wg && wgs[0]) setWg(wgs[0].id);
  }, [wg, wgs]);

  function onSubmit(): void {
    setError(null);
    if (!slug.match(/^[a-z0-9-]+$/)) return setError('slug must be [a-z0-9-]+');
    if (!url.startsWith('https://')) return setError('endpoint URL must use https://');
    if (!trustConfirmed) return setError('must confirm P-A trust check is in place');
    submit({
      slug,
      name,
      description: desc || undefined,
      owning_wg_id: wg,
      backend_type: 'native_mcp',
      backend_url: url,
      risk_tier: risk,
    }).catch((err: unknown) => {
      setError(
        err instanceof ApiError
          ? `${err.status}: ${JSON.stringify(err.body)}`
          : (err as Error).message,
      );
    });
  }

  return (
    <SectionCard
      icon={Globe2}
      title="M-Endpoint"
      text="One screen — paste the URL, confirm trust, submit."
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <HelpCallout
          whatHappens={
            <>
              We add a row to the registry pointing at your URL and emit an audit event. The gateway
              will start routing <code>tools/list</code> and <code>tools/call</code> requests to
              your server for any user whose effective workgroups grant access.{' '}
              <strong>The server must already be running and reachable from the gateway.</strong>
            </>
          }
          youNeed={[
            <>
              Your MCP server up at an <code>https://</code> URL the gateway can hit.
            </>,
            <>
              The P-A trust check wired in your server (validates Bearer JWTs against the gateway's
              JWKS) — see <code>docs/server-authoring/02-trust-model.md</code>.
            </>,
            <>
              An owning workgroup created under <a href="/admin/workgroups">Admin → Workgroups</a>.
            </>,
          ]}
          afterwards={[
            <>
              Click <strong>Validate</strong> to probe the URL and confirm the auth gate is up.
            </>,
            <>
              Test discovery from Claude Desktop using <code>{window.location.origin}/mcp</code>{' '}
              (the gateway URL — not your backend URL).
            </>,
            <>
              Promote from <code>draft</code> → <code>active</code> on the server detail page once
              you've smoke-tested.
            </>,
          ]}
        />
        <RequiredLegend />
        <Field label="Slug" required hint="lowercase, dashes only">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="texttosql"
            style={inputStyle}
            required
          />
        </Field>
        <Field label="Display name" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            required
          />
        </Field>
        <Field label="Description (optional)">
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={2}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
        <Field
          label="Backend URL"
          required
          hint="https://your-server/mcp — must already verify gateway JWKS"
        >
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://texttosql.pochub-ai.com/mcp"
            style={inputStyle}
            required
          />
        </Field>
        <WgSelect wgs={wgs} value={wg} onChange={setWg} required />
        <Field label="Risk tier" required>
          <select
            value={risk}
            onChange={(e) => setRisk(e.target.value as (typeof RISK_TIERS)[number])}
            style={inputStyle}
          >
            {RISK_TIERS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </Field>
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: 10,
            border: '1px solid var(--line)',
            borderRadius: 8,
            fontSize: 14,
          }}
        >
          <input
            type="checkbox"
            checked={trustConfirmed}
            onChange={(e) => setTrustConfirmed(e.target.checked)}
            required
          />
          <span>
            <strong>
              I've configured the P-A trust check
              <Required />
            </strong>{' '}
            — the server validates Bearer JWTs with <code>
              iss={window.location.origin}
            </code> and <code>aud=&lt;backend URL&gt;</code> via the gateway's JWKS. Per
            docs/server-authoring/02-trust-model.md.
          </span>
        </label>
        {checks && <ChecklistPanel checks={checks} />}
        {testResult && <TestConnectionPanel r={testResult} />}
        {error && <ErrorBox msg={error} />}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={() => testConn.mutate({ backend_url: url, backend_type: 'native_mcp' })}
            disabled={testConn.isPending || !url}
            style={btnStyle}
            title="Probe the URL and classify any failure (auth / stateful mode / unreachable). Doesn't write anything to the registry."
          >
            {testConn.isPending ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            onClick={() =>
              validate.mutate({
                mode: 'endpoint',
                slug,
                owning_wg_id: wg,
                backend_url: url,
              })
            }
            disabled={validate.isPending || !slug || !wg || !url}
            style={btnStyle}
          >
            {validate.isPending ? 'Validating…' : 'Validate'}
          </button>
          <button
            type="button"
            className="primary"
            onClick={onSubmit}
            disabled={submitting || !slug || !name || !url || !wg}
            style={primaryBtnStyle}
          >
            <Check size={14} style={{ marginRight: 6 }} />
            {submitting ? 'Registering…' : 'Register'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

/* ─── REST-wrapper wizard ────────────────────────────────────────── */

/* ─── helpers ──────────────────────────────────────────────────────── */

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
        {label}
        {required && <Required />}
      </span>
      {children}
      {hint && (
        <span className="subtle" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function WgSelect({
  wgs,
  value,
  onChange,
  required,
}: {
  wgs: { id: string; display_name: string }[];
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}): JSX.Element {
  return (
    <Field
      label="Owning workgroup"
      required={required}
      hint={
        <>
          Don&apos;t see your workgroup?{' '}
          <a href="/admin/workgroups" target="_blank" rel="noreferrer">
            Open Admin → Workgroups
          </a>{' '}
          to create one (opens in a new tab so you don&apos;t lose this form).
        </>
      }
    >
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        <option value="">— pick a workgroup —</option>
        {wgs.map((w) => (
          <option key={w.id} value={w.id}>
            {w.id} — {w.display_name}
          </option>
        ))}
      </select>
    </Field>
  );
}

function ChecklistPanel({ checks }: { checks: CheckResult[] }): JSX.Element {
  const errs = checks.filter((c) => c.severity === 'error' && !c.ok).length;
  const warns = checks.filter((c) => c.severity === 'warn' && !c.ok).length;
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: 12,
        background: 'rgba(255,255,255,0.5)',
        fontSize: 13,
      }}
    >
      <strong>
        Validation: {checks.length} checks ({errs} error, {warns} warn)
      </strong>
      <ul style={{ margin: '8px 0 0', paddingLeft: 0, listStyle: 'none' }}>
        {checks.map((c) => {
          const color =
            c.severity === 'error' && !c.ok
              ? '#c0392b'
              : c.severity === 'warn' && !c.ok
                ? '#b7791f'
                : '#2f855a';
          const icon = c.ok ? '✓' : c.severity === 'error' ? '✗' : '⚠';
          return (
            <li
              key={c.name}
              style={{ display: 'flex', gap: 8, padding: '3px 0', alignItems: 'flex-start' }}
            >
              <span style={{ color, width: 14, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
              <span style={{ flex: 1 }}>
                <code style={{ marginRight: 6 }}>{c.name}</code>
                <span style={{ color }}>{c.message}</span>
              </span>
            </li>
          );
        })}
      </ul>
      {errs > 0 && (
        <p style={{ margin: '8px 0 0', color: '#c0392b' }}>
          <AlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          Errors block registration. Fix and re-validate.
        </p>
      )}
    </div>
  );
}

function ErrorBox({ msg }: { msg: string }): JSX.Element {
  return (
    <p style={{ color: '#c0392b', fontSize: 14, margin: 0 }} role="alert">
      <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
      {msg}
    </p>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  background: 'transparent',
  color: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid var(--line-strong)',
  background: 'rgba(255,255,255,0.85)',
  fontSize: 14,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
};

const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: '1px solid rgba(140, 21, 21, 0.3)',
  background: 'linear-gradient(135deg, #8c1515, #b83a24)',
  color: '#fff8ec',
  fontWeight: 650,
  fontSize: 14,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  boxShadow: '0 12px 28px rgba(140, 21, 21, 0.22)',
};

/**
 * Result panel for the "Test connection" button. Shows the structured
 * diagnosis returned by /api/v1/servers/test-connection — green on
 * success, amber/red with specific remediation on failure.
 */
function TestConnectionPanel({ r }: { r: TestConnectionResult }): JSX.Element {
  const tone: 'ok' | 'warn' | 'error' = r.ok
    ? 'ok'
    : r.kind === 'rpc_error' || r.kind === 'bad_envelope' || r.kind === 'not_json'
      ? 'warn'
      : 'error';
  const colors = {
    ok: { bg: 'rgba(95,178,122,0.10)', border: '#5fb27a', fg: '#1f5a37' },
    warn: { bg: 'rgba(245,183,0,0.10)', border: '#e8b13a', fg: '#92400e' },
    error: { bg: 'rgba(217,119,102,0.10)', border: '#d97766', fg: '#7a1f1f' },
  }[tone];
  return (
    <div
      role="status"
      style={{
        marginTop: 8,
        padding: '10px 14px',
        borderRadius: 8,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.fg,
        fontSize: 13,
        lineHeight: 1.55,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 14 }}>{r.ok ? '✓ Test passed' : '✗ Test failed'}</strong>
        {r.kind && !r.ok && <code style={{ fontSize: 11, opacity: 0.85 }}>kind={r.kind}</code>}
        {r.status && <code style={{ fontSize: 11, opacity: 0.85 }}>HTTP {r.status}</code>}
        {r.authenticated && r.ok && (
          <span style={{ fontSize: 11, opacity: 0.7 }}>· authenticated probe</span>
        )}
      </div>
      <p style={{ margin: '6px 0 0' }}>{r.summary}</p>
      {r.remediation && <p style={{ margin: '6px 0 0', fontWeight: 600 }}>→ {r.remediation}</p>}
      {r.tools && r.tools.length > 0 && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer' }}>
            Discovered {r.tools.length} tool{r.tools.length === 1 ? '' : 's'}
          </summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
            {r.tools.map((t) => (
              <li key={t.name}>
                <code style={{ fontSize: 11 }}>{t.name}</code>
                {t.title && t.title !== t.name && ` — ${t.title}`}
              </li>
            ))}
          </ul>
        </details>
      )}
      {r.rawError && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.8 }}>
            Raw upstream message
          </summary>
          <pre
            style={{
              margin: '6px 0 0',
              padding: 8,
              background: 'rgba(0,0,0,0.05)',
              borderRadius: 4,
              fontSize: 11,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {r.rawError}
          </pre>
        </details>
      )}
    </div>
  );
}
